// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { Paper, Typography, Button, IconButton, Fade, CircularProgress, MenuItem, InputBase, Badge, Select, Tooltip, Collapse, Divider, List, ListItemIcon, TextField, Menu } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import downloadIcon from 'Assets/download-icon.png';
import aiIcon from 'Assets/ai.png';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DoneIcon from '@mui/icons-material/Done';
import CheckIcon from '@mui/icons-material/Check';
import autonmousIcon from 'Assets/autonomous.png';
import { Box, useTheme } from '@mui/system';
import KmlBufferDialog from 'Features/project/KmlBufferDialog';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ClearDownloadFilesThunk, DeleteDeliverableFileThunk, DeleteDxfFileThunk, DeleteGeoDbFileThunk, DeleteOutputLasFileThunk, DeleteShpFileThunk, DeleteSurfaceFileThunk, DownloadDeliverableFileThunk, DownloadGDBWarningFileThunk, DownloadGeoDbFileThunk, DownloadLASThunk, DownloadPostgisDxfThunk, DownloadShpFileThunk, DownloadSurfaceFileThunk, EditOrderThunk, GenerateCadTemplateJsonFileThunk, GeneratePostgisExportFileThunk, GetOrdersThunk, SendDeliveryEstimateNotificationThunk, SetDownloadFilesState, SetOrderIdThunk, SetOutputLas } from 'Features/order/orderThunk';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SingleFileUploadForm from 'Components/FileUploadForm/SingleFileUploadForm';
import ConfirmDialog from 'Components/ConfirmDialog';
import ConfirmProceedDialog from 'Components/ConfirmProceedDialog';
import { ShowUploadSingleFileDialogAction } from 'Features/fileUpload/fileUploadActions';
import { projectTypes, utilitiesPackagesSubtypes } from 'Utils/constants';
import { RootState } from 'Store';
import { getOrders } from 'Features/order/orderSelectors';
import { GetProjectThunk } from 'Features/project/projectThunk';
import { GetAllBundlesThunk } from 'Features/bundle/bundleThunk';
import { DownloadOriginalKMLThunk } from 'Features/kml/kmlThunk';
import { GetDeliverablesThunk } from 'Features/admin/adminThunk';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { GetOrgTeamsThunk } from 'Features/account/accountThunk';
import LayerSelectionDialog from './layerSelectionDialog';
import KmlFileFormatDialog from './KmlFileFormatDialog';
import GenerateGDBDialog from './GenerateGDBDialog';
import ErrorDialog from './ErrorDialog';
import stylesDef from './styles';
import { EditAddonsDialog } from './EditAddonsDialog';
import { OutOfCreditsDialog } from './OutOfCreditsDialog';

moment.tz.setDefault('America/New_York');
/**
 * Component renders contents of an order card for each order in the project
 */
interface OrderCardProps {
  order: IOrder;
  projectId: string;
  validFile: boolean;
  onRequiredFieldsChange?: (hasMissingFields: boolean) => void;
}

type UpdateData = {
  orderId: string;
  bundleName?: string;
  density?: string;
  deliverySpeed?: string;
  projectType?: string;
  deliveryDate?: Date;
  deliveryEstimate?: Date;
  referenceId?: string;
  team?: string;
};

const OrderCard = ({ order, projectId, validFile, onRequiredFieldsChange }: OrderCardProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const downloadFileStatuses = useAppSelector((state) => state.order?.dxfFileStatus);
  const project = useAppSelector((state) => state.project.project);
  const { orgId } = useAppSelector((state) => state.admin);
  const placedOrder = useAppSelector((state: RootState) => getOrders(state).placedOrder);
  const { allBundles } = useAppSelector((state) => state.bundle);
  const { orgTeams } = useAppSelector((state) => state.account);

  const [uploadFileExtension, setUploadFileExtension] = useState(null);
  const [showLayerSelection, setShowLayerSelection] = useState(false);
  const [bundleDeliverables, setBundleDeliverables] = useState([]);
  const [loadingDeliverables, setLoadingDeliverables] = useState(false);
  const [showEditAddonsDialog, setShowEditAddonsDialog] = useState(false);
  const [showOutOfCreditsDialog, setShowOutOfCreditsDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [adminOutputUpload, setAdminOutputUpload] = useState(false);
  const [showConfirmDeleteXml, setShowConfirmDeleteXml] = useState(false);
  const [showConfirmDeleteGdb, setShowConfirmDeleteGdb] = useState(false);
  const [showConfirmDeleteDeliverable, setShowConfirmDeleteDeliverable] = useState(false);
  const [showConfirmDeleteDxf, setShowConfirmDeleteDxf] = useState(false);
  const [showConfirmDeleteLas, setShowConfirmDeleteLas] = useState(false);
  const [showKmlDownloadDialog, setShowKmlDownloadDialog] = useState(false);
  const [showGenerateGDBDialog, setShowGenerateGDBDialog] = useState(false);
  const [generateGDBLoading, setGenerateGDBLoading] = useState(false);
  const [generateGDBError, setGenerateGDBError] = useState(false);
  // tracking the loading status of the CAD template JSON generation
  const [generateCadTemplateJson, setGenerateCadTemplateJson] = useState(false);
  // tracking any errors that occur during the CAD template JSON generation
  const [generateCadTemplateJsonError, setGenerateCadTemplateJsonError] = useState(false);

  const [orderId, setOrderId] = useState('');
  const [xmlId, setXmlId] = useState('');
  const [deliverableId, setDeliverableId] = useState('');
  const [dxfId, setDxfId] = useState('');
  const [lasId, setLasId] = useState('');
  const [editOrder, setEditOrder] = useState(false);
  const [projectTypeEdit, setProjectTypeEdit] = useState(project?.type || 'standard');
  const [bundleTypeEdit, setBundleTypeEdit] = useState(order.bundleSubtype);
  const [bundleEdit, setBundleEdit] = useState(order?.bundleName);
  const [densityEdit, setDensityEdit] = useState(order?.density || '');
  const [deliveryDateEdit, setDeliveryDateEdit] = useState(order.deliveryDate ? moment(order.deliveryDate) : null);
  const [deliveryEstimateEdit, setDeliveryEstimateEdit] = useState(order.deliveryEstimate ? moment(order.deliveryEstimate) : null);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openEstimateDatePicker, setOpenEstimateDatePicker] = useState(false);
  const [deliverySpeedEdit, setDeliverySpeedEdit] = useState(order?.deliverySpeed || '');
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [download, setDownload] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [basePackage, setBasePackage] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(order?.team);
  const [referenceId, setReferenceId] = useState(order.referenceId || '');
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [fileType, setFileType] = useState('dxf');
  const [showConfirmDeleteShp, setShowConfirmDeleteShp] = useState(false);
  const [shpId, setShpId] = useState('');
  const [gdbError, setGdbError] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { creditAdjustments, bundleName, density, deliverySpeed, customBundle, boundaryFile } = order;
  const disableEdit = !boundaryFile || (customBundle && order.orderStatus > 0);

  const isUtilitiesProject = project?.type === 'utilities';
  const missingRequiredUtilitiesFieldsForEdit = (isUtilitiesProject && !bundleTypeEdit) ||
    (isUtilitiesProject && bundleTypeEdit && !bundleEdit);
  const missingRequiredUtilitiesFieldsForProcessing = (isUtilitiesProject && !order.bundleSubtype) ||
    (isUtilitiesProject && order.bundleSubtype && !order.bundleName);

  const markFieldDirty = (fieldName: string) => {
    setDirtyFields((prev) => new Set(prev).add(fieldName));

    // Handle cascading dependencies
    if (fieldName === 'projectType') {
      // Reset bundle-related fields when project type changes
      setBundleEdit('');
      setBundleTypeEdit('');
      setDirtyFields((prev) => new Set(prev).add('bundleName').add('bundleType'));
    }

    if (fieldName === 'bundleType' && projectTypeEdit === 'utilities') {
      // Reset bundle when bundle type changes for utilities
      setBundleEdit('');
      setDirtyFields((prev) => new Set(prev).add('bundleName'));
    }
  };

  useEffect(() => {
    dispatch(GetAllBundlesThunk());
    dispatch(GetOrgTeamsThunk(orgId));
  }, [orgId]);

  useEffect(() => {
    cancelEditOrderClick();
    // Reset form fields when order data changes
    if (!editOrder) {
      resetOrderEdits();
    }
  }, [order?._id, order?.referenceId, order?.bundleName, order?.density, order?.deliverySpeed, order?.team, order?.deliveryDate, order?.deliveryEstimate]);

  useEffect(() => {
    if (allBundles) {
      if (projectTypeEdit === 'standard') {
        setBasePackage(allBundles.standard);
      } else if (projectTypeEdit === 'utilities' && bundleTypeEdit === 'Underground (2D)') {
        setBasePackage(allBundles.utilities?.underground);
      } else if (projectTypeEdit === 'utilities') {
        setBasePackage(allBundles.utilities?.aerial);
      } else if (projectTypeEdit === 'custom') {
        setBasePackage(allBundles.custom);
      }
    }
  }, [allBundles, projectTypeEdit, bundleTypeEdit]);

  // useMemo
  // disable Download Template JSON + DXF button if order status is 0 or cadTemplate field is not present
  const disableDownloadCadTemplateJson = useMemo(() => order?.orderStatus === 0 || !order?.cadTemplate, [order]);

  const onOkDeleteXml = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteXml(false);
    dispatch(DeleteSurfaceFileThunk(xmlId, orderId));
    e.stopPropagation();
  };
  const onOkDeleteDeliverable = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteDeliverable(false);
    dispatch(DeleteDeliverableFileThunk(deliverableId, orderId));
    e.stopPropagation();
  };

  const onOkDeleteGdb = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteGdb(false);
    dispatch(DeleteGeoDbFileThunk(xmlId, orderId));
    e.stopPropagation();
  };

  const onCancelGdb = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteGdb(false);
    setShowConfirmDeleteGdb(false);
    e.stopPropagation();
  };

  const onOkDeleteShp = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteShp(false);
    dispatch(DeleteShpFileThunk(shpId, orderId));
    e.stopPropagation();
  };

  const onCancelShp = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteShp(false);
    e.stopPropagation();
  };

  const onCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteXml(false);
    e.stopPropagation();
  };

  const onCancelDeleteDeliverable = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteDeliverable(false);
    e.stopPropagation();
  };

  const onOkDeleteDxf = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteDxf(false);
    dispatch(DeleteDxfFileThunk(dxfId, orderId));
    e.stopPropagation();
  };

  const onCancelDxf = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteDxf(false);
    e.stopPropagation();
  };

  const onOkDeleteLas = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteLas(false);
    if (lasId && orderId && projectId) {
      dispatch(DeleteOutputLasFileThunk(lasId, orderId, projectId));
    }
    e.stopPropagation();
  };

  const onCancelLas = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDeleteLas(false);
    e.stopPropagation();
  };

  const PrepareOutputFile = async (oId: string, siteId: string) => {
    await dispatch(SetDownloadFilesState(siteId));
    await dispatch(DownloadPostgisDxfThunk(oId, siteId, true));
  };

  const DownloadOutputFile = async (siteId: string) => {
    window.open(downloadFileStatuses.find((f) => f.siteId === siteId)?.url, '_self');
    await dispatch(ClearDownloadFilesThunk(siteId));
  };

  const onKmlUploadClick = (oId: string) => {
    dispatch(SetOrderIdThunk(oId));
    setUploadFileExtension(['.kml', '.kmz']);
    setAdminOutputUpload(false);
    setShowUploadDialog(true);
  };

  const onDxfUploadClick = (oId: string) => {
    dispatch(SetOrderIdThunk(oId));
    setFileType('dxf');
    setUploadFileExtension(['.dxf', '.laz', '.xml', '.zip', '.shp', '.dwg', '.dgn', '.dem', '.dtm', '.gdb', '.rar']);
    setAdminOutputUpload(true);
    setShowUploadDialog(true);
    handleClose();
  };

  const onGdbUploadClick = (oId: string) => {
    dispatch(SetOrderIdThunk(oId));
    setFileType('gdb');
    setUploadFileExtension(['.gdb', '.zip', '.rar']);
    setAdminOutputUpload(true);
    setShowUploadDialog(true);
    handleClose();
  };

  const onShpUploadClick = (oId: string) => {
    dispatch(SetOrderIdThunk(oId));
    setFileType('shp');
    setUploadFileExtension(['.zip', '.rar']);
    setAdminOutputUpload(true);
    setShowUploadDialog(true);
    handleClose();
  };

  const onOutputFileUploadClick = (oId: string) => {
    dispatch(SetOrderIdThunk(oId));
    setFileType('other');
    setUploadFileExtension(['.laz', '.xml', '.dwg', '.dgn', '.dem', '.dtm']);
    setAdminOutputUpload(true);
    setShowUploadDialog(true);
    dispatch(SetOutputLas(true));
    handleClose();
  };

  useEffect(() => {
    if (showUploadDialog) {
      dispatch(ShowUploadSingleFileDialogAction());
      setShowUploadDialog(false);
    }
  }, [showUploadDialog]);

  const ButtonFunction = (oId: string, cadFile: any) => {
    const siteId = cadFile._id;
    if (downloadFileStatuses.some((obj) => (obj.siteId === siteId && obj.fileReadyStatus.dxfFile === 1))) {
      setDownload(false);
      DownloadOutputFile(siteId);
    } else {
      setDownload(true);
      PrepareOutputFile(oId, siteId);
    }
  };

  const resetOrderEdits = () => {
    setBundleEdit(order?.bundleName || '');
    setDensityEdit(order?.density || '');
    setDeliverySpeedEdit(order?.deliverySpeed || '');
    setProjectTypeEdit(customBundle ? 'custom' : project?.type || 'standard');
    setBundleTypeEdit(order?.bundleSubtype || '');
    setReferenceId(order?.referenceId || '');
    setSelectedTeam(order?.team || '');
    setDeliveryDateEdit(order.deliveryDate ? moment(order.deliveryDate) : null);
    setDeliveryEstimateEdit(order.deliveryEstimate ? moment(order.deliveryEstimate) : null);
    setDirtyFields(new Set());
  };

  // Notify project component when required fields status changes (only based on saved order/updated data)
  useEffect(() => {
    if (onRequiredFieldsChange) {
      onRequiredFieldsChange(missingRequiredUtilitiesFieldsForProcessing);
    }
  }, [missingRequiredUtilitiesFieldsForProcessing, onRequiredFieldsChange]);

  const editOrderClick = (oid: string) => {
    if (oid !== orderId) {
      resetOrderEdits();
    }
    setOrderId(oid);
    setEditOrder(true);
  };

  const cancelEditOrderClick = () => {
    setOrderId('');
    resetOrderEdits();
    setEditOrder(false);
  };

  const editOrderUpdateClick = async () => {
    setIsEditSubmitting(true);

    const updateData: UpdateData = { orderId };

    if (dirtyFields.has('bundleName')) updateData.bundleName = bundleEdit;
    if (dirtyFields.has('density')) updateData.density = densityEdit;
    if (dirtyFields.has('deliverySpeed')) updateData.deliverySpeed = deliverySpeedEdit;
    if (dirtyFields.has('projectType')) updateData.projectType = projectTypeEdit;
    if (dirtyFields.has('deliveryDate')) updateData.deliveryDate = deliveryDateEdit?.toDate();
    if (dirtyFields.has('deliveryEstimate')) updateData.deliveryEstimate = deliveryEstimateEdit?.toDate();
    if (dirtyFields.has('referenceId')) updateData.referenceId = referenceId;
    if (dirtyFields.has('team')) updateData.team = selectedTeam;

    await dispatch(EditOrderThunk(updateData));
    await dispatch(GetProjectThunk());
    await dispatch(GetOrdersThunk(projectId));
    setOrderId('');
    setEditOrder(false);
    setIsEditSubmitting(false);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const onGenerateGDBClick = async () => {
    if (order?.cadFiles.length > 1) {
      setShowGenerateGDBDialog(true);
    } else if (order?.cadFiles[0]?._id) {
      setGenerateGDBLoading(true);
      const result = await dispatch(GeneratePostgisExportFileThunk(order?.cadFiles[0]?._id, '.gdb'));
      if (result?.error) {
        setGenerateGDBError(true);
      }
      setGenerateGDBLoading(false);
    }
  };
  // Function to handle the generation of cad temmplate json file
  const onDownloadCadTemplateJson = async () => {
    if (order?.orderStatus > 0) {
      setGenerateCadTemplateJson(true);
      const result = await dispatch(GenerateCadTemplateJsonFileThunk(order?.cadTemplate));
      if (result?.error) {
        setGenerateCadTemplateJsonError(true);
      }
      setGenerateCadTemplateJson(false);
    }
  };

  const onEditAddonsClick = async () => {
    setLoadingDeliverables(true);
    setShowEditAddonsDialog(true);
    const response = await dispatch(GetDeliverablesThunk(order._id, projectId));
    setBundleDeliverables(response);
    setLoadingDeliverables(false);
  };

  const handleDeliveryDateChange = (value: moment.Moment) => {
    setDeliveryDateEdit(value);
    markFieldDirty('deliveryDate');
    setOpenDatePicker(false);
  };

  const handleDeliveryEstimateChange = (value: moment.Moment) => {
    setDeliveryEstimateEdit(value);
    markFieldDirty('deliveryEstimate');
    setOpenEstimateDatePicker(false);
  };

  const handleSendDeliveryEstimateNotification = async () => {
    setSendingNotification(true);
    await dispatch(SendDeliveryEstimateNotificationThunk(order._id));
    setSendingNotification(false);
    setShowNotifyDialog(false);
  };

  const onDownloadAllInputFilesClick = () => {
    dispatch(DownloadOriginalKMLThunk(order._id));
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderReferenceId = (isEditOrder: boolean) => {
    if (isEditOrder) {
      return (
        <TextField
          name="name"
          variant="standard"
          placeholder="Enter Reference ID"
          value={referenceId ?? ''}
          sx={styles.textFiled}
          onChange={(e) => {
            setReferenceId(e.target.value);
            markFieldDirty('referenceId');
          }}
        />
      );
    }

    return referenceId ? <Typography>{referenceId}</Typography> : <Typography>N/A</Typography>;
  };

  const renderTeam = (isEditOrder: boolean) => {
    const teamObj = orgTeams.find((team) => team._id === order.team);

    if (isEditOrder) {
      return (
        <Select
          variant="outlined"
          input={<InputBase style={{ width: '100%' }} />}
          value={selectedTeam ?? ''}
          onChange={(e) => {
            setSelectedTeam(e.target.value);
            markFieldDirty('team');
          }}
          disabled={disableEdit}
        >
          {
            orgTeams.map((team) => <MenuItem value={team?._id}>{team?.name}</MenuItem>)
          }
        </Select>
      );
    }

    return teamObj?.name ? <Typography>{teamObj?.name}</Typography> : <Typography>N/A</Typography>;
  };

  return (
    <>
      <Paper sx={styles.card}>
        {/* display order submitter only if order status is greater than 0
        if order is submitted by an AirWorks admin, display the admin email */}
        <Box sx={styles.orderDetails}>
          <Typography variant="h6" sx={styles.itemWrapper}>Order Details </Typography>
          <Box sx={styles.detailsTexts}>
            <Typography sx={styles.itemWrapper}>
              Submitter:
              {' '}
              {` ${order.submittedByAwAdmin || order.ownerEmail || 'N/A'}`}
            </Typography>
            <Typography>
              Order created by:
              {' '}
              {` ${order.ownerEmail || 'N/A'}`}
            </Typography>
          </Box>
        </Box>
        <Divider sx={styles.sectionDivider} />
        <Typography sx={styles.itemWrapper}>{`${order.ownerOrganization}/${projectId}/${order._id}`}</Typography>
        {!!order?.additionalInstructions && (
          <>
            <Box sx={styles.warningFile}>
              <InfoIcon sx={styles.finishedIcon} />
              <Typography variant="h6" sx={styles.additionalInstructions}>Additional Instructions:</Typography>
            </Box>
            <Typography sx={styles.additionalInstructionsText}>{order.additionalInstructions}</Typography>
          </>
        )}
        <Box sx={styles.orderInfoWrapper}>
          <Box>
            <Box sx={styles.inputTitleWrapper}>
              <Typography variant="h4">Input Files</Typography>
              <Button onClick={() => onKmlUploadClick(order._id)} color="primary">Upload Files</Button>
            </Box>
            <Box sx={styles.cadFileList}>
              {order.boundaryFile && <Typography>{order.boundaryFile.split('/').pop()}</Typography>}
              {order.boundaryFile && (
                <IconButton onClick={() => setShowKmlDownloadDialog(true)}>
                  <img src={downloadIcon} alt="Download .kml" />
                </IconButton>
              )}
            </Box>
            <Button onClick={onDownloadAllInputFilesClick} color="primary" sx={styles.gdbButton}>DOWNLOAD ALL INPUT FILES</Button>
            <Box sx={styles.inputTitleWrapper}>
              <Typography variant="h4">Output Files</Typography>
              <Button disabled={!validFile} onClick={handleClick} color="primary">Upload Files</Button>
              <Menu
                id="file-type-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'file-type-menu',
                }}
              >
                <MenuItem sx={styles.menuItem} onClick={() => onDxfUploadClick(order._id)}>.DXF</MenuItem>
                <MenuItem sx={styles.menuItem} onClick={() => onShpUploadClick(order._id)}>.SHP (zip)</MenuItem>
                <MenuItem sx={styles.menuItem} onClick={() => onGdbUploadClick(order._id)}>.GDB</MenuItem>
                <MenuItem sx={styles.menuItem} onClick={() => onOutputFileUploadClick(order._id)}>OTHER</MenuItem>
              </Menu>
            </Box>
            <Box sx={styles.outputFilesWrapper}>
              {(order.outputLasFiles && order.outputLasFiles.length > 0) && order.outputLasFiles.map((file) => (
                <Box sx={styles.cadFileList} key={file._id}>
                  <Typography>{file.filename && file.filename.split('/').pop()}</Typography>
                  <Box>
                    <IconButton onClick={() => {
                      setShowConfirmDeleteLas(true);
                      setLasId(file._id);
                      setOrderId(order._id);
                    }}
                    >
                      <ClearIcon sx={styles.deleteIcon} />
                    </IconButton>
                    <IconButton onClick={() => dispatch(DownloadLASThunk(order._id, file.filename.split('/').pop()))}>
                      <img src={downloadIcon} alt="Download .las" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {order?.surfaceFiles?.map((file) => (
                <Box sx={styles.cadFileList} key={file._id}>
                  <Typography>{file.filename}</Typography>
                  <Box>
                    <IconButton onClick={() => {
                      setShowConfirmDeleteXml(true);
                      setXmlId(file._id);
                      setOrderId(order._id);
                    }}
                    >
                      <ClearIcon sx={styles.deleteIcon} />
                    </IconButton>
                    <IconButton onClick={() => dispatch(DownloadSurfaceFileThunk(file._id, order._id))}>
                      <img src={downloadIcon} alt="Download .xml" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {order?.deliverableFiles?.map((file) => (
                <Box sx={styles.cadFileList} key={file._id}>
                  <Typography>{file.filename}</Typography>
                  <Box>
                    <IconButton onClick={() => {
                      setShowConfirmDeleteDeliverable(true);
                      setDeliverableId(file._id);
                      setOrderId(order._id);
                    }}
                    >
                      <ClearIcon sx={styles.deleteIcon} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        dispatch(DownloadDeliverableFileThunk(file._id, order._id));
                      }}
                    >
                      <img src={downloadIcon} alt={`Download (${file.fileType})`} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {order?.geoDbFiles?.map((file) => (
                <Box key={file._id}>
                  <Box sx={styles.cadFileList}>
                    <Typography>{file.filename}</Typography>
                    <Box>
                      <IconButton onClick={() => {
                        setShowConfirmDeleteGdb(true);
                        setXmlId(file._id);
                        setOrderId(order._id);
                      }}
                      >
                        <ClearIcon sx={styles.deleteIcon} />
                      </IconButton>
                      <IconButton onClick={() => dispatch(DownloadGeoDbFileThunk(file._id, order._id))}>
                        <img src={downloadIcon} alt="Download .zip" />
                      </IconButton>
                    </Box>
                  </Box>
                  {file.warningFileName && (
                    <Box sx={styles.cadFileList}>
                      <Box sx={styles.warningFile}>
                        <InfoIcon sx={styles.infoIcon} />
                        <Typography>{file.warningFileName}</Typography>
                      </Box>
                      <Box>
                        <IconButton onClick={() => dispatch(DownloadGDBWarningFileThunk(file._id))}>
                          <img src={downloadIcon} alt="Download warning.txt" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
              {order?.cadFiles?.map((file) => (
                <Box sx={styles.cadFileList} key={file._id}>
                  <Box sx={styles.cadFileName}>
                    {file.expressAIUpload && (
                      <Tooltip title="Express AI" arrow placement="top">
                        <img src={aiIcon} alt="Express AI Upload" />
                      </Tooltip>
                    )}
                    {file.autonomous && (
                      <Tooltip title="Autonomous" arrow placement="top">
                        <img src={autonmousIcon} alt="Autonomous" />
                      </Tooltip>
                    )}
                    <Typography>{file.name}</Typography>
                  </Box>
                  {/* TODO - move this logic outside return - Ishan 06/23/2021 */}
                  {downloadFileStatuses.some((obj) => (obj.siteId === file._id && obj.fileReadyStatus.dxfFile === 1) && download) ? (
                    <Badge color="primary" overlap="circle" variant="dot">
                      <IconButton onClick={() => ButtonFunction(order._id, file)}>
                        <img src={downloadIcon} alt="Download .dxf" />
                      </IconButton>
                    </Badge>
                  ) :
                    (downloadFileStatuses.some((obj) => (
                      obj.siteId === file._id && obj.fileReadyStatus.dxfFile === 0)) && download) ?
                        <CircularProgress size={24} sx={styles.downloadProgress} /> : (
                        <Box>
                            <IconButton onClick={() => {
                            setShowConfirmDeleteDxf(true);
                            setDxfId(file._id);
                            setOrderId(order._id);
                          }}
                          >
                            <ClearIcon sx={styles.deleteIcon} />
                          </IconButton>
                            <IconButton onClick={() => ButtonFunction(order._id, file)}>
                            <img src={downloadIcon} alt="Download .dxf" />
                          </IconButton>
                          </Box>
                      )}
                </Box>
              ))}
              {order?.shpFiles?.map((file) => (
                <Box sx={styles.cadFileList} key={file._id}>
                  <Box sx={styles.cadFileName}>
                    <Typography>{file.filename.split('/').pop()}</Typography>
                  </Box>
                  <Box>
                    <IconButton onClick={() => {
                      setShowConfirmDeleteShp(true);
                      setShpId(file._id);
                      setOrderId(order._id);
                    }}
                    >
                      <ClearIcon sx={styles.deleteIcon} />
                    </IconButton>
                    <IconButton onClick={() => dispatch(DownloadShpFileThunk(file._id, order._id))}>
                      <img src={downloadIcon} alt="Download .shp" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              <Box>
                {(order?.cadFiles?.some((file) => (file.expressAIUpload === true && !file.expressAIEmailSent))) && (
                  <Button
                    onClick={() => { setShowLayerSelection(true); }}
                    color="primary"
                  >
                    SEND EMAIL
                  </Button>
                )}
                {(order?.cadFiles?.some((file) => file.expressAIEmailSent)) &&
                  <Typography variant="h5" sx={styles.emailText}>EMAIL SENT</Typography>}
              </Box>
            </Box>
            <Box sx={styles.buttonContainer}>
              {
                generateGDBLoading ?
                  <CircularProgress size={24} /> :
                  <Button disabled={!order?.cadFiles.length} onClick={onGenerateGDBClick} color="primary" sx={styles.gdbButton}>Generate GDB</Button>
              }
            </Box>
            {order?.cadTemplateFile?.retainDuplicates && (
              <Box sx={styles.duplicateContainer}>
                <InfoIcon sx={styles.infoIcon} />
                <Typography>Do not run “de-duplicator”</Typography>
              </Box>
            )}
            <Box>
              {/* // TODO: use orderStatusCode constants instead of hardcoded values */}
              {
                generateCadTemplateJson ?
                  <CircularProgress size={24} /> :
                  // disable Download Template JSON + DXF button if order status is 0 or cadTemplate field is not present
                  <Button disabled={disableDownloadCadTemplateJson} onClick={onDownloadCadTemplateJson} color="primary" sx={styles.gdbButton}>Download Template JSON + DXF</Button>
              }
            </Box>
            <Box sx={styles.inputTitleWrapper}>
              <Typography variant="h4">Layer Deliverables</Typography>
              <Button
                variant="text"
                onClick={onEditAddonsClick}
                color="primary"
                disabled={order?.orderStatus === 7 || order?.orderStatus === 8 || disableEdit}
              >
                Edit Add-ons
              </Button>
            </Box>
            <Box>
              {
                order?.deliverables?.map((deliverable:any) => (
                  <List sx={styles.bundleListItem} key={deliverable._id}>
                    <ListItemIcon sx={styles.listItemIcon}>
                      <CheckIcon sx={styles.finishedIcon} />
                    </ListItemIcon>
                    <Typography>{deliverable.name}</Typography>
                  </List>
                ))
              }
            </Box>
          </Box>
          <Box>
            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Project Type</Typography>
              {
                editOrder ? (
                  <Select
                    variant="outlined"
                    input={<InputBase style={{ width: '100px' }} />}
                    value={projectTypeEdit || project.type}
                    onChange={(e) => {
                      setProjectTypeEdit(e.target.value);
                      markFieldDirty('projectType');
                    }}
                    disabled={!!placedOrder || disableEdit}
                  >
                    {
                      projectTypes.map((type) => <MenuItem key={type} value={type} sx={styles.projectType}>{type === 'standard' ? 'AEC' : type}</MenuItem>)
                    }
                  </Select>
                ) :
                  <Typography sx={styles.projectType}>{customBundle ? 'custom' : project.type || 'standard'}</Typography>
              }

              {(project?.type === 'utilities' || projectTypeEdit === 'utilities') && (
                <>
                  <Typography sx={styles.itemWrapper}>Bundle Type</Typography>
                  {
                    editOrder ? (
                      <Tooltip title={bundleTypeEdit || order.bundleSubtype || ''} placement="top">
                        <Select
                          variant="outlined"
                          input={<InputBase style={!bundleTypeEdit ? styles.requiredFieldHighlight : styles.normalFieldWidth} />}
                          value={bundleTypeEdit}
                          onChange={(e) => {
                            setBundleTypeEdit(e.target.value);
                            markFieldDirty('bundleType');
                          }}
                          disabled={disableEdit}
                        >
                          {
                            utilitiesPackagesSubtypes.map((type) => <MenuItem key={type} value={type} sx={styles.projectType}>{type}</MenuItem>)
                          }
                        </Select>
                      </Tooltip>
                    ) :
                      <Typography sx={styles.projectType}>{order.bundleSubtype}</Typography>
                  }
                </>
              )}

              <Typography sx={styles.itemWrapper}>Base Package</Typography>
              {editOrder ? (
                <Tooltip title={bundleEdit || bundleName || ''} placement="top">
                  <Select
                    variant="outlined"
                    input={(
                      <InputBase style={
                        (project?.type === 'utilities' || projectTypeEdit === 'utilities') && bundleTypeEdit && !bundleEdit
                          ? styles.requiredFieldHighlight
                          : styles.normalFieldWidth
                      }
                      />
                    )}
                    value={bundleEdit || bundleName}
                    onChange={(e) => {
                      if (e.target.value === 'Topo') {
                        setDensityEdit('normal');
                        markFieldDirty('density');
                      }
                      setBundleEdit(e.target.value);
                      markFieldDirty('bundleName');
                    }}
                    disabled={disableEdit}
                  >
                    {
                      basePackage?.map((bundle) => <MenuItem value={bundle}>{bundle}</MenuItem>)
                    }
                  </Select>
                </Tooltip>
              ) :
                <Typography>{bundleName || 'N/A'}</Typography>}
            </Box>
            <Divider sx={styles.sectionDivider} />
            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Reference ID</Typography>
              {renderReferenceId(editOrder)}
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Team</Typography>
              {renderTeam(editOrder)}
            </Box>

            {order?.opsJiraId && (
              <Box sx={styles.gridChild}>
                <Typography sx={styles.itemWrapper}>Ops Jira ID</Typography>
                <Typography
                  component="a"
                  href={`https://airworksus.atlassian.net/browse/${order.opsJiraId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={styles.jiraLink}
                >
                  {order.opsJiraId}
                </Typography>
              </Box>
            )}

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Date Created</Typography>
              {order.createdAt ? <Typography>{moment(order.createdAt).format('MM/DD/YYYY')}</Typography> : <Typography>N/A</Typography>}
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Date Processed</Typography>
              {order.orderDate ? <Typography>{moment(order.orderDate).format('MM/DD/YYYY')}</Typography> : <Typography>N/A</Typography>}
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Est. Delivery Date</Typography>
              {editOrder ?
                (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      open={openEstimateDatePicker}
                      minDate={moment(project.createdAt)}
                      sx={styles.datePicker}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <IconButton onClick={() => setOpenEstimateDatePicker(!openEstimateDatePicker)} sx={styles.arrowDropDown}>
                                <ArrowDropDownIcon />
                              </IconButton>
                            ),
                          },
                        },
                      }}
                      onChange={handleDeliveryEstimateChange}
                      value={deliveryEstimateEdit}
                    />
                  </LocalizationProvider>
                )
                :
                (
                  <Box sx={styles.deliveryDateContainer}>
                    {order.orderStatus > 0 && order.deliveryEstimate && (
                      <Button
                        size="small"
                        startIcon={<NotificationsActiveIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setShowNotifyDialog(true)}
                        sx={styles.notifyButton}
                      >
                        Notify
                      </Button>
                    )}
                    <Typography>{order.deliveryEstimate ? moment(order.deliveryEstimate).format('MM/DD/YYYY') : 'N/A'}</Typography>
                  </Box>
                )}
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Delivery Date</Typography>
              {editOrder ?
                (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      open={openDatePicker}
                      minDate={moment(project.createdAt)}
                      sx={styles.datePicker}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <IconButton onClick={() => setOpenDatePicker(!openDatePicker)} sx={styles.arrowDropDown}>
                                <ArrowDropDownIcon />
                              </IconButton>
                            ),
                          },
                        },
                      }}
                      onChange={handleDeliveryDateChange}
                      value={deliveryDateEdit}
                    />
                  </LocalizationProvider>
                )
                :
                (
                  <Typography>{order.deliveryDate ? moment(order.deliveryDate).format('MM/DD/YYYY') : 'N/A'}</Typography>
                )}
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Kickoff Date</Typography>
              {order.anticipatedKickoffDate ? <Typography>{moment.utc(order.anticipatedKickoffDate).format('MM/DD/YYYY')}</Typography> : <Typography>N/A</Typography>}
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Order Acres</Typography>
              <Typography>{ order.acreage || 'N/A'}</Typography>
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Processing Area Length</Typography>
              <Typography>{order.kmlLength || 'N/A'}</Typography>
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Order Linear Feet</Typography>
              <Typography>{order.orderLinearFeet || 'N/A'}</Typography>
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Density</Typography>
              {(() => {
                const showDensitySelect = (editOrder) && ((!bundleEdit ? bundleName !== 'Topo' : bundleEdit !== 'Topo'));
                if (showDensitySelect) {
                  return (
                    <Select
                      variant="outlined"
                      input={<InputBase style={{ width: '100px' }} />}
                      value={densityEdit || density}
                      onChange={(e) => {
                        setDensityEdit(e.target.value);
                        markFieldDirty('density');
                      }}
                      disabled={disableEdit}
                    >
                      {
                        ['normal', 'low'].map((d) => <MenuItem value={d}>{d}</MenuItem>)
                      }
                    </Select>
                  );
                }
                return <Typography>{(!densityEdit ? density : densityEdit)}</Typography>;
              })()}
            </Box>

            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Delivery Speed</Typography>
              {editOrder ? (
                <Select
                  variant="outlined"
                  input={<InputBase style={{ width: '100px' }} />}
                  value={deliverySpeedEdit || deliverySpeed}
                  onChange={(e) => {
                    setDeliverySpeedEdit(e.target.value);
                    markFieldDirty('deliverySpeed');
                  }}
                  disabled={disableEdit}
                >
                  {
                    ['normal', 'expedited', 'super_expedited'].map((speed) => <MenuItem value={speed}>{speed?.split('_').join(' ')}</MenuItem>)
                  }
                </Select>
              ) :
                <Typography>{deliverySpeed?.split('_').join(' ') || 'N/A'}</Typography>}
            </Box>
            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>Initial Order Credits</Typography>
              <Typography>{order.initialCredits ? order.initialCredits.toFixed(2) : 'N/A'}</Typography>
            </Box>
            <Box sx={styles.layersAndDeliverablesWrapper}>
              {order.addOns?.length > 0 && (
                <>
                  <Box sx={styles.sectionHeader}>
                    <Typography
                      sx={styles.layersAndDeliverablesTitle}
                      variant="h4"
                    >
                      LAYERS & DELIVERABLES
                    </Typography>
                    {isExpanded ? (
                      <Button onClick={handleToggle}>
                        <ArrowDropUpIcon sx={styles.arrowIcon} />
                      </Button>
                    ) : (
                      <Button onClick={handleToggle}>
                        <ArrowDropDownIcon sx={styles.arrowIcon} />
                      </Button>
                    )}
                  </Box>
                  <Divider sx={styles.sectionDivider} />
                  <Collapse in={isExpanded}>
                    <Box>
                      <Box>
                        {order.addOns.map((addOn, i) => {
                          if (addOn.type === 'deliverable') {
                            return (
                              <Box key={addOn.name} sx={styles.addOnItem}>
                                <DoneIcon sx={styles.doneIcon} />
                                <Typography sx={styles.addOnName}>
                                  {addOn.name}
                                </Typography>
                                <Typography sx={styles.addOnAcres}>
                                  {addOn.acres ? `${addOn.acres.toFixed(2)} credits` : 'N/A credits'}
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        })}
                      </Box>
                      <Divider sx={styles.sectionDivider} />
                      <Box>
                        {order.addOns.map((addOn, i) => {
                          if (addOn.type === 'layer') {
                            return (
                              <Box key={addOn.name} sx={styles.addOnItem}>
                                <DoneIcon sx={styles.doneIcon} />
                                <Typography sx={styles.addOnName}>
                                  {addOn.name}
                                </Typography>
                                <Typography sx={styles.addOnAcres}>
                                  {addOn.acres ? `${addOn.acres.toFixed(2)} credits` : 'N/A credits'}
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        })}
                      </Box>
                    </Box>
                  </Collapse>
                </>
              )}
              <Box sx={styles.addOnItem}>
                <Typography variant="h5" sx={styles.totalCreditTitle}>
                  Total Credits Charged
                </Typography>
                {editOrder ? (
                  <Typography sx={styles.totalCredits}>-</Typography>
                ) : (
                  <Typography sx={styles.totalCredits}>
                    {order.creditAcreage ? order.creditAcreage.toFixed(2) : 'N/A'}
                    {' '}
                    credits
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={styles.gridChild}>
              <Typography sx={styles.itemWrapper}>
                Adjusted Credits
              </Typography>
              <Typography>
                { (order.adjustedAcres ?? 0).toFixed(2) }
              </Typography>
            </Box>
            {creditAdjustments?.length > 0 && (
              <ul style={styles.adjustmentList}>
                {creditAdjustments.map((a, i) => (
                  <li key={a._id}>
                    <Box sx={styles.creditAdjustmentItemWrapper}>
                      <p style={styles.adjustmentNumber}>
                        {a.adjustment >= 0 ? '+' : ''}
                        {a.adjustment}
                      </p>
                      <p>{a.reason}</p>
                    </Box>
                  </li>
                ))}
              </ul>
            )}
            {editOrder ? (
              <Box sx={styles.buttonGroup}>
                <Button sx={styles.editOrderButtonWrapper} onClick={cancelEditOrderClick} color="primary">Cancel</Button>
                <Button
                  sx={styles.editOrderButtonWrapper}
                  onClick={editOrderUpdateClick}
                  disabled={
                    isEditSubmitting ||
                    dirtyFields.size === 0 ||
                    missingRequiredUtilitiesFieldsForEdit
                  }
                  color="primary"
                >
                  <Fade in={isEditSubmitting}>
                    <CircularProgress size={24} sx={styles.buttonProgress} />
                  </Fade>
                  <Fade in={!isEditSubmitting}>
                    <span>Update</span>
                  </Fade>
                </Button>
              </Box>
            ) : (
              <Button
                sx={styles.editOrderButtonWrapper}
                onClick={() => editOrderClick(order._id)}
                disabled={!boundaryFile}
                color="primary"
              >
                Edit Order
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      <LayerSelectionDialog
        showDialog={showLayerSelection}
        onCancel={() => { setShowLayerSelection(false); }}
        orderId={order._id}
      />
      <KmlFileFormatDialog
        order={order}
        showDialog={showKmlDownloadDialog}
        onCancel={() => setShowKmlDownloadDialog(false)}
      />
      {uploadFileExtension && <SingleFileUploadForm fileExtension={uploadFileExtension} adminOutputUpload={adminOutputUpload} fileType={fileType} setGdbError={setGdbError} />}
      <ConfirmDialog
        showDialog={showConfirmDeleteXml}
        onOk={onOkDeleteXml}
        onCancel={onCancel}
        contentText="Selected surface file will be deleted"
      />
      <ConfirmDialog
        showDialog={showConfirmDeleteDeliverable}
        onOk={onOkDeleteDeliverable}
        onCancel={onCancelDeleteDeliverable}
        contentText="Selected deliverable file will be deleted"
      />
      <ConfirmDialog
        showDialog={showConfirmDeleteDxf}
        onOk={onOkDeleteDxf}
        onCancel={onCancelDxf}
        contentText="Selected dxf file will be deleted"
      />
      <ConfirmDialog
        showDialog={showConfirmDeleteGdb}
        onOk={onOkDeleteGdb}
        onCancel={onCancelGdb}
        contentText="Selected geodatabase file will be deleted"
      />
      <ConfirmDialog
        showDialog={showConfirmDeleteShp}
        onOk={onOkDeleteShp}
        onCancel={onCancelShp}
        contentText="Selected shp file will be deleted"
      />
      <ConfirmDialog
        showDialog={showConfirmDeleteLas}
        onOk={onOkDeleteLas}
        onCancel={onCancelLas}
        contentText="Selected las file will be deleted"
      />
      <ConfirmProceedDialog
        showDialog={showNotifyDialog}
        onOk={handleSendDeliveryEstimateNotification}
        onCancel={() => setShowNotifyDialog(false)}
        title="Send New Delivery Date Notification"
        contentText={(
          <>
            Clicking
            {' '}
            <strong>&apos;Send&apos;</strong>
            {' '}
            will notify the customer of their new estimated delivery date via email and in-app message.
          </>
        )}
        cancelButtonText="Cancel"
        okButtonText={sendingNotification ? 'Sending...' : 'Send'}
      />
      <EditAddonsDialog
        open={showEditAddonsDialog}
        setOpen={setShowEditAddonsDialog}
        orderId={order?._id}
        projectId={projectId}
        orderAcreage={order?.acreage}
        orderStatus={order?.orderStatus}
        bundleDeliverables={bundleDeliverables}
        loadingDeliverables={loadingDeliverables}
        setShowOutOfCreditsDialog={setShowOutOfCreditsDialog}
      />
      <OutOfCreditsDialog open={showOutOfCreditsDialog} setOpen={setShowOutOfCreditsDialog} />
      <KmlBufferDialog />
      <GenerateGDBDialog showDialog={showGenerateGDBDialog} cadFiles={order?.cadFiles} onCancel={() => setShowGenerateGDBDialog(false)} setError={setGenerateGDBError} />
      <ErrorDialog message="Unable to generate GDB please contact Algorithms for further troubleshooting." showDialog={generateGDBError} onCancel={() => setGenerateGDBError(false)} />
      <ErrorDialog message="Unable to generate Cad Template Json please contact Webapp team for further troubleshooting." showDialog={generateCadTemplateJsonError} onCancel={() => setGenerateCadTemplateJsonError(false)} />
      <ErrorDialog message="Output .gdb should have .gdb in the filename" showDialog={gdbError} onCancel={() => setGdbError(false)} />
    </>
  );
};

export default OrderCard;
